import { fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { mergeLocationsInfo } from '../../utils/locations'
import { getLanguageCode } from '../../utils/translation'
import { getLocationsData, getLocationSuggestions } from '../../utils/api'
import {
  GET_LOCATIONS_INFO,
  getLocationsInfoSuccess,
  GET_LOCATION_SUGGESTIONS,
  getLocationSuggestionsSuccess,
  loadLocationsInfoFromCacheSuccess,
  LocationsInfo,
  KEY_LOCATIONS_INFO,
  SAVE_LOCATION_INFO,
  saveLocationInfoSuccess,
} from '../actions/locationManager'
import { selectCachedLocations } from '../selectors/locationManager'
import { getKeywordLocationsSuccess } from '../../containers/KeywordLists/actions'

let controller = null
const ControllerSingleton = () => {
  if (!controller) {
    controller = new AbortController()
  }

  return controller
}

export default function* locationManager() {
  yield fork(loadCachedLocationsInfo)
  yield takeEvery(GET_LOCATIONS_INFO.action, (action) =>
    getLocationsInfoHandler(
      action.locIds,
      action.onComplete,
      action.isKeywordLocations,
    ),
  )
  yield takeLatest(GET_LOCATION_SUGGESTIONS.action, (action) =>
    getLocationSuggestionsHandler(action.query, action.onComplete),
  )
  yield takeEvery(SAVE_LOCATION_INFO.action, (action) =>
    saveLocationInfoHandler(action.location),
  )
}

function* loadCachedLocationsInfo() {
  try {
    const locationsStr = LocationsInfo.getItem(KEY_LOCATIONS_INFO)

    if (locationsStr) {
      yield put(
        loadLocationsInfoFromCacheSuccess(
          mergeLocationsInfo(JSON.parse(locationsStr), []),
        ),
      )
    } else {
      yield put(loadLocationsInfoFromCacheSuccess({}))
    }
  } catch (e) {
    yield put(loadLocationsInfoFromCacheSuccess({}))
  }
}

function* getLocationsInfoHandler(
  locIds,
  onComplete,
  isKeywordLocations = false,
) {
  let locations = yield select(selectCachedLocations())
  try {
    const lang = getLanguageCode()

    if (locIds && locIds.length > 0) {
      const res = yield getLocationsData(locIds, lang)
      const enRes = yield getLocationsData(locIds)

      locations = yield select(selectCachedLocations())

      if (res.data && enRes.data) {
        const addedEnglishNameLocations = res.data.map((location) => ({
          ...location,
          en: enRes.data.find(
            (englishLocation) => englishLocation.id === location.id,
          )?.name,
        }))
        const newLocations = mergeLocationsInfo(
          locations,
          addedEnglishNameLocations,
        )
        yield put(getLocationsInfoSuccess(newLocations))
        LocationsInfo.setItem(KEY_LOCATIONS_INFO, JSON.stringify(newLocations))
        if (onComplete) onComplete(res.data)
      } else {
        yield put(getLocationsInfoSuccess(locations))
        if (onComplete) onComplete([])
      }

      if (isKeywordLocations) yield put(getKeywordLocationsSuccess())
    } else {
      yield put(getLocationsInfoSuccess(locations))
      if (onComplete) onComplete([])
    }
  } catch (e) {
    yield put(getLocationsInfoSuccess(locations))
    if (onComplete) onComplete([])
  }
}

function* getLocationSuggestionsHandler(query, onComplete) {
  const locations = yield select(selectCachedLocations())
  if (controller) {
    controller.abort()
    controller = null
  }

  const controllerInstance = ControllerSingleton()

  try {
    const lang = getLanguageCode()
    const res = yield getLocationSuggestions(
      query,
      lang,
      controllerInstance.signal,
    )
    const enRes =
      lang !== 'en'
        ? yield getLocationSuggestions(query, 'en', controllerInstance.signal)
        : res

    yield put(getLocationSuggestionsSuccess(locations))

    const dataArray = res.data
      ? res.data.map((data) => {
          const enData = enRes.data.find(({ id: enId }) => enId === data.id)

          return {
            ...data,
            en: enData?.name,
          }
        })
      : []

    if (onComplete) onComplete(dataArray)
  } catch (error) {
    console.error('error', error)
    yield put(getLocationSuggestionsSuccess(locations))
    if (onComplete) onComplete([])
  }
}

function* saveLocationInfoHandler(location) {
  if (!location) return
  const locations = yield select(selectCachedLocations())
  const newLocations = mergeLocationsInfo(locations, [location])

  yield put(saveLocationInfoSuccess(newLocations))
  LocationsInfo.setItem(KEY_LOCATIONS_INFO, JSON.stringify(newLocations))
}
